// SEQ-180 follow-up (14 September 2026): the shape the loading state draws.
//
// WHY THIS IS DERIVED AND NOT DRAWN. The first version of the skeleton
// hardcoded four quarter tiles and two half-width charts and its own comment
// claimed it matched the dashboard. It did not match any particular dashboard:
// measured against the default one, the skeleton grid ended around y=630 and
// the settled grid around y=1390, so the page jumped 760px when the data
// landed and took the wages note with it. A loading state that moves the page
// is worse than no loading state, because the reader starts reading and then
// has the page pulled out from under them.
//
// The real layout is known before any request is made: the page derives
// `gridWidgets` from the saved rows (or from the code-defined default
// dashboard) and builds the REQUEST PLAN out of it, so by definition every
// widget's span exists before the first fetch. This turns that list into
// placeholder tiles, one per widget, at its own size.

import type { PnlWidgetLayout } from "../../lib/pnl/pnlWidgetSchema";

/** What one placeholder tile needs to know: its span, and whether to draw a card or a chart. */
export interface SkeletonTile {
  key: string;
  layout: PnlWidgetLayout;
  /** A single number draws as a KPI tile; everything else draws as a chart card. */
  kind: "kpi" | "chart";
}

export interface SkeletonSource {
  id: string;
  chartType: string;
  layout: PnlWidgetLayout;
}

/**
 * The shape a dashboard of `widgets` will settle into.
 *
 * FALLBACK, AND WHY IT IS THE DEFAULT DASHBOARD'S SHAPE. A dashboard with no
 * widgets yet still has to show something while it finds that out. Four KPI
 * tiles and two charts is the default dashboard's own opening, so the guess is
 * only ever wrong for the case where nothing was saved, and it is right for
 * the case every new user sees first.
 */
export function skeletonTiles(widgets: readonly SkeletonSource[]): SkeletonTile[] {
  if (widgets.length === 0) {
    return [
      ...[0, 1, 2, 3].map((i) => ({ key: `fallback-kpi-${i}`, layout: { w: 1, h: 1 } as PnlWidgetLayout, kind: "kpi" as const })),
      ...[0, 1].map((i) => ({ key: `fallback-chart-${i}`, layout: { w: 2, h: 1 } as PnlWidgetLayout, kind: "chart" as const })),
    ];
  }
  return widgets.map((widget) => ({
    key: widget.id,
    layout: widget.layout,
    kind: widget.chartType === "KPI" ? "kpi" : "chart",
  }));
}
